import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

const BASE_URL = 'https://quidory.com';
const LOGO_URL = 'https://quidory.com/media/quidory-logo.png';

/**
 * `path` should be the locale-agnostic path (e.g. '/features', '/'),
 * and SEO will emit distinct ar/en canonical alternates pointing to
 * BASE_URL/{lang}{path}.
 */
const SEO = ({
  titleEn,
  titleAr,
  descEn,
  descAr,
  path = '/',
  type = 'website',
}) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const title = isAr ? titleAr : titleEn;
  const desc = isAr ? descAr : descEn;

  // Normalize path — strip leading slash for joining, then rebuild
  const tail = path === '/' ? '' : path.startsWith('/') ? path : `/${path}`;
  const arUrl = `${BASE_URL}/ar${tail}`;
  const enUrl = `${BASE_URL}/en${tail}`;
  const canonical = isAr ? arUrl : enUrl;
  // x-default points to the AR root variant (project default language)
  const xDefault = `${BASE_URL}/ar${tail}`;

  return (
    <Helmet>
      <html lang={language} dir={isAr ? 'rtl' : 'ltr'} />
      <title>{title}</title>
      <meta name="description" content={desc} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />

      {/* Hreflang — distinct URLs per locale */}
      <link rel="alternate" hrefLang="ar" href={arUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={xDefault} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={LOGO_URL} />
      <meta property="og:site_name" content="Quidory" />
      <meta property="og:locale" content={isAr ? 'ar_SA' : 'en_US'} />
      <meta property="og:locale:alternate" content={isAr ? 'en_US' : 'ar_SA'} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={LOGO_URL} />
    </Helmet>
  );
};

export default SEO;
